/**
 * @file PDFTextExtractor.ts
 * @description 
 * Utility module for extracting text from PDF documents directly within the browser.
 * Provides two primary methods for extraction:
 * 1. Native Extraction (Fast, reads the embedded text layer of a digital PDF).
 * 2. OCR Fallback (Slower, renders the PDF into an image and uses Tesseract.js).
 */

import * as pdfjsLib from 'pdfjs-dist';
import Tesseract from 'tesseract.js';

// or let it fallback gracefully if the module load fails in CRA.
// Using unpkg with explicit .min.js for standard compatibility.
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

/**
 * METHOD 1: Native Text Extraction
 * Attempts to read the inherent text layer of the PDF.
 * 
 * @param file - The PDF file object obtained from an HTML file input
 * @returns A promise that resolves to the extracted text as a single string
 */
export async function extractTextFromPDF(file: File): Promise<string> {
    console.log(`[PDF.js] Starting native text extraction: ${file.name}`);
    
    try {
        const arrayBuffer = await file.arrayBuffer();
        
        // Load the PDF document into memory
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;
        
        let fullText = "";

        // Iterate through all pages of the parsed document
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            
            // Map over the text items on the page and concatenate them with spaces
            const pageText = textContent.items
                .map((item: any) => item.str)
                .join(" ");
                
            fullText += pageText + "\n\n";
        }

        console.log(`[PDF.js] Native extraction finished, characters: ${fullText.length}`);
        return fullText.trim();
        
    } catch (error: any) {
        console.error(`[PDF.js] Native extraction failed`, error);
        throw new Error(`Native PDF text extraction failed: ${error.message}`);
    }
}

/**
 * METHOD 2: OCR Fallback (Tesseract)
 * Utilized when native extraction fails or returns insufficient text.
 * 
 * @param file - The PDF file object obtained from an HTML file input
 * @returns A promise that resolves to the extracted text obtained via OCR
 */
export async function extractTextViaOCR(file: File): Promise<string> {
    console.log(`[Tesseract OCR] Starting OCR: ${file.name}`);
    
    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let fullText = "";

        // Initialize the Tesseract worker without noisy progress logging
        const worker = await Tesseract.createWorker('ces');

        try {
            // Iterate through all pages
            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                const page = await pdf.getPage(pageNum);

                // Set the viewport scale
                const viewport = page.getViewport({ scale: 2.0 });

                // Create a temporary canvas element in memory
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');

                if (!context) {
                    throw new Error("Unable to create a 2D canvas context");
                }

                canvas.width = viewport.width;
                canvas.height = viewport.height;

                // Render the specific PDF page onto the canvas context
                await page.render({
                    canvasContext: context,
                    viewport: viewport
                }).promise;

                const imageDataUrl = canvas.toDataURL('image/png');

                // Execute the OCR analysis
                const { data: { text } } = await worker.recognize(imageDataUrl);

                fullText += text + "\n\n";
            }
        } finally {
            // Ensure the worker (and its WASM/thread resources) is always released,
            // even if a page fails to render or OCR throws mid-batch.
            await worker.terminate();
        }

        console.log(`[Tesseract OCR] OCR finished, characters: ${fullText.length}`);

        return fullText.trim();

    } catch (error: any) {
        console.error(`[Tesseract OCR] OCR failed`, error);
        throw new Error(`OCR extraction (Tesseract) failed: ${error.message}`);
    }
}

/**
 * Decides whether an extracted text layer is usable or the document should go through OCR.
 * Scanned PDFs often return nothing or only a few "ghost" characters.
 */
export function isTextUsable(text: string | null | undefined, minLetters = 200): boolean {
    if (!text) return false;
    const compact = text.replace(/\s/g, "");
    if (compact.length === 0) return false;
    const letters = (compact.match(/\p{L}/gu) ?? []).length;
    return letters >= minLetters && letters / compact.length >= 0.5;
}

/** FileReader based text read (works in all browsers and in the jsdom test environment). */
export function readFileAsText(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error ?? new Error("Unable to read file"));
        reader.readAsText(file, "utf-8");
    });
}

/**
 * Reads the text of a screening input: plain text files directly, PDFs via the text
 * layer with an OCR fallback. `onStage` reports progress for the UI.
 */
export async function readDocumentText(file: File, onStage?: (stage: "text" | "ocr") => void): Promise<string> {
    const isPlainText = file.type.startsWith("text/") || /\.(txt|md)$/i.test(file.name);
    if (isPlainText) {
        onStage?.("text");
        return readFileAsText(file);
    }

    onStage?.("text");
    let text = "";
    try {
        text = await extractTextFromPDF(file);
    } catch (error) {
        console.warn(`[Extraction] Native extraction failed for ${file.name}, falling back to OCR`, error);
    }
    if (isTextUsable(text)) return text;

    onStage?.("ocr");
    const ocrText = await extractTextViaOCR(file);
    // Keep whichever source produced more text.
    return ocrText.trim().length > text.trim().length ? ocrText : text;
}
